import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationAttributesEndpointIdentifier } from './use-get-source-brand-location-attributes-api';

const endpoint = 'ums/v1/crm/management/source-brand-location-attributes';
export const deleteSourceBrandLocationAttributeEndpointIdentifier = endpoint;

type TData = void;
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, number>;
type ReturnType = UseMutationResult<TData, TError, number>;

export function useDeleteSourceBrandLocationAttributeApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async (id: number) => {
    try {
      await http.delete(`${endpoint}/${id}`);

      toast({
        description: 'Location attribute deleted successfully',
        status: 'success',
      });
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: () => {
      // Only invalidate attribute queries, not location queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationAttributesEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });

      // Invalidate all location-specific attribute queries since we don't know which location this attribute belongs to
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, type] = query.queryKey;
          return queryEndpoint === endpoint && type === 'location';
        },
      });
    },
    ...otherOptions,
  });
}
